<header class="header__content">
  <div class="header__left">

    @if (!appService?.isWide && !(inboundRedirectService?.isInboundRedirectUIContext)) {
      <button
        type="button"
        aria-label="Toggle menu navigation"
        matIconButton
        aria-controls="mobile-menu-sidebar"
        [attr.aria-expanded]="mobileMenuOpened"
        [disabled]="preview"
        (click)="onClick_toggleMobileMenu()">
        <mat-icon>menu</mat-icon>
      </button>
    }

    @if (!(inboundRedirectService?.isInboundRedirectUIContext)) {
      <div class="header__logo">
        @if (!preview && !editMode) {
          <a routerLink="/">
            <img [src]="logoSource" [alt]="siteTitle + ' Home'">
          </a>
        }
        @if (preview || editMode) {
          <img
            [src]="logoSource"
            [alt]="siteTitle + ' Home'"
            [class.editable-logo]="editMode"
            [tabindex]="editMode ? 0 : -1"
            (click)="onLogoClick()"
            (keydown.enter)="onLogoClick()"
            (keydown.space)="onLogoClick()"
            [attr.role]="editMode ? 'button' : null"
            [matTooltip]="editMode ? 'Click to upload new logo (PNG, at least 56 pixels tall)' : ''">
        }
      </div>

      @if (appService?.isWide) {
        @if (menuItems?.length === 1) {
          @let singlePageUnderMaintenance = isPageUnderMaintenance(menuItems[0].pageId);
          <!-- disabledInteractive keeps the greyed, aria-disabled anchor focusable; without an
          explicit tabIndex a disabled anchor is dropped from the tab order entirely. -->
          <a matButton
            disabledInteractive
            [tabIndex]="0"
            [disabled]="singlePageUnderMaintenance"
            [routerLink]="singlePageUnderMaintenance ? null : '/search/' + menuItems[0].pageId"
            [attr.aria-current]="railShowing
              ? null
              : appService?.ariaCurrent('/search/' + menuItems[0].pageId)">
            {{ menuItems[0].label }}
            @if (singlePageUnderMaintenance) {
              <span>{{ siteMetadataService.maintenanceNotice }}</span>
            }
          </a>
        } @else {
          <button matButton
            [matMenuTriggerFor]="pagenav">
            Online Services
            <mat-icon iconPositionEnd>keyboard_arrow_down</mat-icon>
          </button>
          <mat-menu #pagenav="matMenu" class="page-navigation gcr-page-navigation">
            <!-- A maintenance-flagged entry renders disabled: MatMenuItem's disabled state carries
            aria-disabled, so the entry stays in the accessibility tree and a screen reader reads it
            as unavailable. MatMenuItem has no disabledInteractive in this Material version, so the
            item is skipped by arrow keys and typeahead; the notice below is what carries the reason
            to anyone who reads it. -->
            @for (item of menuItems; track item.menuItemId) {
              @let underMaintenance = isPageUnderMaintenance(item.pageId);
              <a mat-menu-item
                [disabled]="underMaintenance"
                [routerLink]="preview || underMaintenance ? null : '/search/' + item.pageId"
                [attr.aria-current]="railShowing
                  ? null
                  : appService?.ariaCurrent('/search/' + item.pageId)">
                <div class="page-navigation__wrapper">
                  {{ item.label }}
                  <span class="page-navigation__description">{{ item.description }}</span>
                  @if (underMaintenance) {
                    <span class="page-navigation__description">
                      {{ siteMetadataService.maintenanceNotice }}
                    </span>
                  }
                </div>
              </a>
            }
          </mat-menu>
        }
      }
    }
    @if (inboundRedirectService?.isInboundRedirectUIContext) {
      <div class="header__logo">
        <img [src]="logoSource" [alt]="siteTitle + ' Logo'">
      </div>
    }
  </div>

  @if (inboundRedirectService?.isInboundRedirectUIContext) {
    <nav aria-label="Primary" class="navigation">
      <!-- The receipt page gets a high-emphasis filled button -->
      @if (isCheckoutSuccessPage) {
        <button matButton="filled"
          (click)="onClick_cancelOrCompletePayment()">
          <span>{{returnToSenderLabel}}</span>
        </button>
      }
      <!-- All other pages (currently limited to the checkout page) get a text button -->
      @if (!isCheckoutSuccessPage) {
        <button matButton
          (click)="onClick_cancelOrCompletePayment()">
          <span>{{returnToSenderLabel}}</span>
        </button>
      }
    </nav>
  }

  <!-- Wide only: the logo above is itself a link home at every width, so this button is a
  shorthand rather than the only way back. Narrow has no room to spend on one — the same row
  already carries the menu, cart, help and account controls — so its Home entry is the first
  row of the mobile menu instead. -->
  @if (appService?.isWide && !(inboundRedirectService?.isInboundRedirectUIContext)) {
    <a matIconButton
      aria-label="Home"
      matTooltip="Home"
      [routerLink]="preview ? null : '/'"
      [attr.aria-current]="railShowing ? null : appService?.ariaCurrent('/', true)">
      <mat-icon>home</mat-icon>
    </a>
  }

  <!-- Unlike the other end-of-row buttons, the cart toggle is NOT gated on the inbound redirect
  context: the cart auto-opens there after an item is added and this button is the only way to
  reopen it (parity with the floating cart button it replaced). -->
  <button [disabled]="preview"
    data-guide="cart-button"
    (click)="onClick_toggleCart()"
    matIconButton
    aria-controls="cart-sidebar"
    [attr.aria-expanded]="cartOpened"
    [attr.aria-label]="cartToggleAriaLabel"
    matTooltip="Toggle Cart">
    <mat-icon
      aria-hidden="false"
      [matBadge]="cartItemCount"
      [matBadgeHidden]="cartItemCount < 1">shopping_cart</mat-icon>
  </button>

  @if (!(inboundRedirectService?.isInboundRedirectUIContext)) {
    <button [disabled]="preview"
      (click)="onClick_toggleHelp()"
      matIconButton
      aria-controls="help-sidebar"
      [attr.aria-expanded]="helpOpened"
      aria-label="Help"
      data-guide="help-button"
      matTooltip="Help">
      <mat-icon>help</mat-icon>
    </button>
  }

  @if (authenticationEnabled && !(inboundRedirectService?.isInboundRedirectUIContext)) {
    @if (!authService?.isSignedIn) {
      @if (appService?.isWide) {
        <a matButton="outlined"
          class="header__sign-in"
          [routerLink]="preview ? null : '/sign-in'"
          [attr.aria-current]="appService?.ariaCurrent('/sign-in')">
          <mat-icon>account_circle</mat-icon>
          Sign In
        </a>
      } @else {
        <a matIconButton
          aria-label="Sign in"
          matTooltip="Sign In"
          [routerLink]="preview ? null : '/sign-in'"
          [attr.aria-current]="appService?.ariaCurrent('/sign-in')">
          <mat-icon>account_circle</mat-icon>
        </a>
      }
    } @else {
      @if (appService?.isWide) {
        <button matIconButton class="header__avatar-button"
          [matMenuTriggerFor]="profilenav"
          [matTooltip]="userService?.currentUser?.name || 'Account'"
          [attr.aria-label]="(userService?.currentUser?.name || 'User') + ': User profile'">
          <span class="header__avatar" aria-hidden="true">{{userInitials}}</span>
        </button>
      } @else {
        <button matIconButton class="header__avatar-button"
          (click)="onClick_toggleAccountMenu()"
          aria-controls="account-sidebar"
          [attr.aria-expanded]="accountMenuOpened"
          [matTooltip]="userService?.currentUser?.name || 'Account'"
          [attr.aria-label]="(userService?.currentUser?.name || 'User') + ': User profile'">
          <span class="header__avatar" aria-hidden="true">{{userInitials}}</span>
        </button>
      }
      <mat-menu #profilenav="matMenu" class="gcr-account-menu">
        <div class="account-menu">
          <span class="account-menu__name">{{ this.userService?.currentUser?.name }}</span>
          <span class="account-menu__email"
            [matTooltip]="this.userService?.currentUser?.email">
            {{ this.userService?.currentUser?.email }}
          </span>
        </div>
        <mat-divider></mat-divider>
        <a mat-menu-item
          routerLink="/profile"
          [attr.aria-current]="accountSettingsPage ? 'page' : null">Manage Profile</a>
        <button mat-menu-item
          [disabled]="preview"
          (click)="onClick_signOut()">Sign Out</button>
      </mat-menu>
    }
  }
</header>
